import { useEffect, useMemo, useRef } from "react";

import { useQueryParams } from "./useQueryParams";

type UseQueryParamsNonNumericTypes = string | boolean | undefined | object;

function pickKnownKeys<T extends Record<string, UseQueryParamsNonNumericTypes>>(
  source: Partial<T> | Record<string, unknown>,
  knownKeys: string[] | null
): Partial<T> {
  if (!knownKeys) return source as Partial<T>;
  const out: Record<string, unknown> = {};
  for (const key of knownKeys) {
    const value = (source as Record<string, unknown>)[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as Partial<T>;
}

/**
 * Wraps useQueryParams with localStorage persistence.
 * On mount, if no known-key URL params exist, restores from localStorage
 * (or falls back to provided defaults). Persists known-key filters to
 * localStorage whenever they change.
 *
 * Storage and restore are SCOPED to the keys present in `defaultFilters`.
 * Any other URL params (e.g. `omnisearch` from OmnisearchDataGrid) are
 * intentionally ignored so this hook doesn't accidentally over-persist
 * sibling state from the same URL.
 *
 * @param storageKey - localStorage key for persisting filters
 * @param defaultFilters - fallback filters when nothing is saved; also
 *                        defines which keys this hook owns
 */
export function usePersistedFilters<T extends Record<string, UseQueryParamsNonNumericTypes>>(
  storageKey: string,
  defaultFilters?: Partial<T>
) {
  const { filters, setFilters, navigateWithFilters } = useQueryParams<T>();
  const hasInitialized = useRef(false);
  const prevStorageKey = useRef(storageKey);

  // Reset initialization when the storage key changes (e.g. tenant switch)
  // so that filters are re-read from the new key.
  if (prevStorageKey.current !== storageKey) {
    prevStorageKey.current = storageKey;
    hasInitialized.current = false;
  }

  // Known keys: the set of fields this hook is responsible for. Derived
  // from `defaultFilters` so callers only need to declare their schema
  // once. If a caller doesn't pass defaults, fall back to whole-URL
  // semantics for backwards compatibility (no current caller does this).
  const knownKeys = useMemo(
    () => (defaultFilters ? Object.keys(defaultFilters) : null),
    [defaultFilters]
  );

  useEffect(() => {
    if (hasInitialized.current) return;

    const knownFilters = pickKnownKeys<T>(filters, knownKeys);
    const hasAnyKnownQuery = Object.values(knownFilters).some((v) => v !== undefined);
    if (hasAnyKnownQuery) {
      localStorage.setItem(storageKey, JSON.stringify(knownFilters));
      hasInitialized.current = true;
      return;
    }

    // No known-key URL params — restore from localStorage or fall back to defaults
    let defaults = defaultFilters;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = pickKnownKeys<T>(JSON.parse(stored), knownKeys);
        if (Object.keys(parsed).length > 0) {
          defaults = parsed;
        }
      }
    } catch {
      // ignore malformed localStorage
    }

    if (defaults) {
      setFilters((state) => ({ ...state, ...defaults }));
    }
    hasInitialized.current = true;
  }, [filters, setFilters, storageKey, defaultFilters, knownKeys]);

  // Persist known-key filters to localStorage whenever they change.
  // storageKey is read from a ref so that a key change (tenant switch) does
  // not re-trigger this effect and write stale filters to the new key.
  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;
  useEffect(() => {
    const knownFilters = pickKnownKeys<T>(filters, knownKeys);
    if (Object.values(knownFilters).some((v) => v !== undefined)) {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(knownFilters));
    }
  }, [filters, knownKeys]);

  return { filters, setFilters, navigateWithFilters };
}
